import { Controller } from '@hotwired/stimulus';
import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  size,
  offset
} from '@floating-ui/dom';
import { useHotkeys } from 'stimulus-use/hotkeys';

const CLASS_NAME_SHOW = '-is-active';

let submenu;
let dropdown;

export default class extends Controller {
  static targets = ['trigger', 'menu', 'input'];
  static values = {
    placement: {
      type: String,
      default: 'bottom-start'
    },
    strategy: {
      type: String,
      default: 'absolute'
    },
    submenu: {
      type: Boolean,
      default: false
    },
    cloneMenu: {
      type: Boolean,
      default: false
    }
  };

  connect() {
    useHotkeys(this, {
      esc: [this.hide]
    });

    this.originalParent = this.element;

    this.menuElement = this.menuTarget;
    this.triggerElement = this.triggerTarget;

    this.menuElement.style.position = this.strategyValue;
    this.innerMenuEl = this.menuElement.querySelector(
      '.y-dropdown__menu-inner'
    );
    this.setTransformOrigin(this.placementValue);

    this.triggerElement.setAttribute('aria-haspopup', 'true');
    this.triggerElement.setAttribute('aria-expanded', 'false');

    this.isCloned = false;
  }

  get safeMenuTarget() {
    return this.menuElement || this.menuTarget;
  }

  get safeTriggerTarget() {
    return this.triggerElement || this.triggerTarget;
  }

  findNearestForm() {
    let element = this.element;
    while (element && element !== document.body) {
      if (element.tagName === 'FORM') {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  getCloneTarget() {
    if (this.submenuValue) {
      const nearestForm = this.findNearestForm();
      if (nearestForm) {
        return nearestForm;
      }
    }

    return document.body;
  }

  setTransformOrigin(placement) {
    const transformOriginValues = {
      'bottom-end': 'top right',
      'bottom-start': 'top left',
      'top-end': 'bottom right',
      'top-start': 'bottom left',
      bottom: 'top center',
      top: 'bottom center',
      'right-end': 'left top',
      'right-start': 'left bottom',
      'left-end': 'right top',
      'left-start': 'right bottom',
      default: 'top right'
    };
    const transformOrigin =
      transformOriginValues[placement] || transformOriginValues.default;
    this.innerMenuEl.style.transformOrigin = transformOrigin;
  }

  calcPosition = async () => {
    const applySize = ({ availableWidth, availableHeight }) => {
      if (this.innerMenuEl && this.innerMenuEl.style) {
        Object.assign(this.innerMenuEl.style, {
          maxWidth: `${availableWidth}px`,
          maxHeight: `${availableHeight}px`,
          overflowY: 'auto'
        });
      }
    };

    const middleware = [
      offset(4),
      flip(),
      shift({ padding: 6 }),
      size({ apply: applySize })
    ];

    this.popper = await computePosition(
      this.safeTriggerTarget,
      this.safeMenuTarget,
      {
        placement: this.placementValue,
        strategy: this.strategyValue,
        middleware: middleware
      }
    ).then(({ x, y, strategy }) => {
      Object.assign(this.safeMenuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: strategy,
        zIndex: this.submenuValue ? 10001 : 9999
      });
    });
  };

  handleOutsideClick = (e) => {
    if (!this.isShown()) return;

    if (e.target.closest('[data-controller="dropdown"]')) return;
    if (e.target.closest('.y-dropdown__menu')) return;
    if (e.target.closest('.flatpickr-calendar')) return;

    this.hide();
  };

  toggle(event) {
    if (event) event.preventDefault();
    return this.isShown() ? this.hide() : this.show();
  }

  show() {
    if (this.submenuValue) {
      if (submenu) submenu.hide();
      submenu = this;
    } else {
      if (dropdown) dropdown.hide();
      dropdown = this;
    }

    if (this.submenuValue || (this.cloneMenuValue && !this.submenuValue)) {
      const cloneTarget = this.getCloneTarget();
      cloneTarget.appendChild(this.safeMenuTarget);
      this.isCloned = true;
      this.cloneTarget = cloneTarget;
    }

    this.safeTriggerTarget.classList.add(CLASS_NAME_SHOW);
    this.safeTriggerTarget.setAttribute('aria-expanded', 'true');
    this.safeMenuTarget.classList.add(CLASS_NAME_SHOW);
    this.floatingInstance = autoUpdate(
      this.safeTriggerTarget,
      this.safeMenuTarget,
      this.calcPosition
    );
    this.dispatch('show');

    if (!this.submenuValue) {
      document.addEventListener('click', this.handleOutsideClick);
    }
  }

  hide = () => {
    if (!this.isShown()) return;
    this.safeTriggerTarget.classList.remove(CLASS_NAME_SHOW);
    this.safeTriggerTarget.setAttribute('aria-expanded', 'false');
    this.safeMenuTarget.classList.remove(CLASS_NAME_SHOW);

    if (this.isCloned) {
      if (
        this.originalParent &&
        this.originalParent.contains &&
        !this.originalParent.contains(this.safeMenuTarget)
      ) {
        this.originalParent.appendChild(this.safeMenuTarget);
      }
      this.isCloned = false;
      this.cloneTarget = null;
    }

    if (this.floatingInstance) {
      this.floatingInstance();
      this.floatingInstance = undefined;
    }

    if (!this.submenuValue) {
      document.removeEventListener('click', this.handleOutsideClick);
    }

    this.closeSubmenus();

    this.dispatch('hide');
  };

  isShown() {
    return (
      this.safeMenuTarget &&
      this.safeMenuTarget.classList.contains(CLASS_NAME_SHOW)
    );
  }

  closeSubmenus = () => {
    const dropdowns = this.element.querySelectorAll(
      '[data-controller="dropdown"]'
    );

    dropdowns.forEach((el) => {
      const controller = this.application.getControllerForElementAndIdentifier(
        el,
        'dropdown'
      );
      if (controller) controller.hide();
    });
  };

  disconnect() {
    if (this.isCloned) {
      if (
        this.originalParent &&
        this.safeMenuTarget &&
        !this.originalParent.contains(this.safeMenuTarget)
      ) {
        this.originalParent.appendChild(this.safeMenuTarget);
      }
    }

    if (submenu === this) submenu = undefined;
    if (dropdown === this) dropdown = undefined;

    this.hide();
  }
}
